import { v2, v3 } from "oc-threejs/short3";
import * as Utils from "../../commons/Utils";
import React from "react";
import { Matrix3, Vector2, Vector3 } from "three";
import * as Maths3 from "oc-threejs/Maths3";

export interface CoordinateSystem {
  pointAt: (s: Vector2) => void;
  pointToScreen: (grundrissPoint: Vector3) => Vector2;
  pointToGrundriss: (screenPoint: Vector2, y?: number) => Vector3;
  sizeToScreen: (worldSize: Vector3) => Vector2;
  sizeToGrundriss: (screenSize: Vector3, y: number) => Vector3;
  lengthToScreen: (worldLength: number) => number;
  lengthToGrundriss: (screenLength: number) => number;
  zoomTo: (zoomFactor: number, screenPoint: Vector2) => void;
  resetZoom: () => void;
  setCameraTo(worldPoint: Vector3);
  canvasRect: DOMRect;
  zoomUnitInM: number;
}

// Einfache orthografische Projektion in Screen-Space

export function coordinateSystem(
  grundriss = APP.arrangement.grundriss,
  canvasRect = (
    document.querySelector(".stage2d") || APP.renderer.domElement
  ).getBoundingClientRect()
): CoordinateSystem {
  let camera, scale, scaleV2, screenMiddle, translateV2, projectM3, unprojectM3;

  function resetFromCamera() {
    camera = APP.camera;
    const fov = (camera.getEffectiveFOV() * Math.PI) / 180;
    const factor = Math.max(canvasRect.width, 1) / (2 * Math.tan(fov / 2));
    scale = factor / camera.position.y;
    scaleV2 = v2(scale, scale);
    screenMiddle = v2(canvasRect.width / 2, canvasRect.height / 2);
    translateV2 = Maths3.v3To2xz(camera.position).clone().negate();

    projectM3 = new Matrix3()
      .translate(translateV2.x, translateV2.y)
      .scale(scaleV2.x, scaleV2.y)
      .translate(screenMiddle.x, screenMiddle.y);

    unprojectM3 = projectM3.clone().invert();
  }

  resetFromCamera();

  function pointToScreen(grundrissPoint: Vector3): Vector2 {
    return Maths3.v3To2xz(grundrissPoint).applyMatrix3(projectM3);
  }

  function pointToGrundriss(screenPoint: Vector2, y = 0): Vector3 {
    const v = screenPoint.clone().applyMatrix3(unprojectM3);
    return v3(v.x, y, v.y);
  }

  function sizeToScreen(worldSize: Vector3): Vector2 {
    const s = Maths3.v3To2xz(worldSize).multiply(scaleV2);
    return v2(s.x, s.y);
  }

  function sizeToGrundriss(screenSize: Vector3, y = 0): Vector3 {
    const v = screenSize.clone().divide(scaleV2);
    return v3(v.x, y, v.y);
  }

  function lengthToScreen(worldLength: number): number {
    return Math.abs(worldLength * scale);
  }

  function lengthToGrundriss(screenLength: number): number {
    return Math.abs(screenLength / scale);
  }

  function zoomTo(zoomFactor: number, screenPoint: Vector2 = screenMiddle) {
    const p = pointToGrundriss(screenPoint);
    const cameraPosition = () => camera.position.clone();
    const delta = cameraPosition().sub(p);
    const dir = delta.clone().multiplyScalar(zoomFactor).sub(delta);
    setCameraTo(cameraPosition().add(dir));
  }

  function resetZoom() {
    const cameraPosition = camera.position.clone();
    cameraPosition.y = 32;
    setCameraTo(cameraPosition);
  }

  function setCameraTo(p: Vector3) {
    // Begrenzung der Kamera-Position wenn Objekte vorhanden sind
    const grundriss = APP.arrangement.grundriss;
    if (grundriss.ecken.length > 0 || APP.arrangement.aufbauten.length > 0) {
      const puffer = 10; // 10 Meter Puffer um die bereits vorhandenen Grundriss-Objekte
      const boundingBox = grundriss.boundingBox.clone();
      boundingBox.expandByScalar(puffer);

      // Begrenze die X- und Z-Koordinaten auf die erweiterte BoundingBox
      p.x = Math.max(boundingBox.min.x, Math.min(boundingBox.max.x, p.x));
      p.z = Math.max(boundingBox.min.z, Math.min(boundingBox.max.z, p.z));
    }

    camera.position.copy(p);
    APP.controls.target.copy(p).setY(0);
    APP.controls.update();
    resetFromCamera();
  }

  const zoomUnitInM = Math.pow(10, Math.floor(Math.log10(lengthToGrundriss(1000))));

  const pointAt = (s: Vector2): void => {
    Utils.reactRenderStatic(
      <div
        style={{
          position: "absolute",
          backgroundColor: "red",
          width: "4px",
          height: "4px",
          left: s.x + "px",
          top: s.y + "px",
        }}
      ></div>
    ).then((c) => document.querySelector(".stage2d").appendChild(c));
  };

  return {
    pointAt,
    pointToScreen,
    pointToGrundriss,
    sizeToScreen,
    sizeToGrundriss,
    lengthToScreen,
    lengthToGrundriss,
    canvasRect,
    zoomUnitInM,
    zoomTo,
    resetZoom,
    setCameraTo,
  };
}
